import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Skeleton,
  Alert,
  AlertTitle,
  TextField,
  InputAdornment,
  IconButton,
  Breadcrumbs,
  Link,
  Divider,
  Tooltip,
  Stack,
  Avatar,
  useTheme,
  alpha,
  Tab,
  Tabs,
} from '@mui/material';
import {
  AccountBalanceWallet as WalletIcon,
  CreditCard as CreditCardIcon,
  Receipt as ReceiptIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  NavigateNext as NavigateNextIcon,
  Home as HomeIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Autorenew as AutorenewIcon,
  Star as StarIcon,
  ShoppingCart as ShoppingCartIcon,
  History as HistoryIcon,
  Subscriptions as SubscriptionsIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { API_BASE_URL } from '../../constants';

// ─── Type Definitions ────────────────────────────────────────────────────────

interface PaymentRecord {
  id: string;
  date: string;
  description: string;
  amount: number;
  status: 'completed' | 'pending' | 'failed' | 'refunded' | 'processing';
  method: string;
  reference?: string;
  type?: string;
}

interface CreditBalance {
  balance: number;
  currency: string;
  lastUpdated?: string;
}

interface Subscription {
  id: string;
  name: string;
  plan: string;
  status: 'active' | 'cancelled' | 'paused' | 'expired';
  amount: number;
  interval: string;
  nextBillingDate: string;
  startDate: string;
  features?: string[];
}

interface CreditPackage {
  id: string;
  name: string;
  credits: number;
  price: number;
  currency: string;
  description?: string;
  popular?: boolean;
  bonus?: number;
  features?: string[];
}

interface PaymentOverview {
  totalSpent: number;
  activeSubscriptions: number;
  creditBalance: number;
  pendingPayments: number;
}

type PaymentStatusFilter = 'all' | 'completed' | 'pending' | 'failed' | 'refunded' | 'processing';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatCurrency = (amount: number): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const formatDate = (dateStr: string): string => {
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
};

const formatDateTime = (dateStr: string): string => {
  try {
    return new Date(dateStr).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
};

const statusConfig: Record<
  string,
  { color: 'success' | 'warning' | 'error' | 'info' | 'default'; icon: React.ReactElement; label: string }
> = {
  completed: { color: 'success', icon: <CheckCircleIcon fontSize="small" />, label: 'Completed' },
  pending: { color: 'warning', icon: <WarningIcon fontSize="small" />, label: 'Pending' },
  failed: { color: 'error', icon: <ErrorIcon fontSize="small" />, label: 'Failed' },
  refunded: { color: 'info', icon: <InfoIcon fontSize="small" />, label: 'Refunded' },
  processing: { color: 'default', icon: <AutorenewIcon fontSize="small" />, label: 'Processing' },
};

// ─── Component ───────────────────────────────────────────────────────────────

const PaymentPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { token } = useAuth();
  const { showSuccess, showError } = useNotification();

  // State
  const [activeTab, setActiveTab] = useState(0);
  const [isLoadingOverview, setIsLoadingOverview] = useState(true);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [isLoadingSubscriptions, setIsLoadingSubscriptions] = useState(true);
  const [isLoadingPackages, setIsLoadingPackages] = useState(true);
  const [overviewError, setOverviewError] = useState<string | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [subscriptionsError, setSubscriptionsError] = useState<string | null>(null);
  const [packagesError, setPackagesError] = useState<string | null>(null);

  const [overview, setOverview] = useState<PaymentOverview>({
    totalSpent: 0,
    activeSubscriptions: 0,
    creditBalance: 0,
    pendingPayments: 0,
  });
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [creditPackages, setCreditPackages] = useState<CreditPackage[]>([]);

  // Table state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<PaymentStatusFilter>('all');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // ─── API Helpers ─────────────────────────────────────────────────────────

  const authHeaders = useMemo(
    () => ({
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    }),
    [token],
  );

  const apiFetch = useCallback(
    async <T,>(url: string): Promise<T> => {
      const res = await fetch(url, { headers: authHeaders });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      const json = await res.json();
      return json.data ?? json;
    },
    [authHeaders],
  );

  // ─── Data Fetching ──────────────────────────────────────────────────────

  const fetchOverview = useCallback(async () => {
    setIsLoadingOverview(true);
    setOverviewError(null);
    try {
      const [balanceRes, historyRes] = await Promise.allSettled([
        apiFetch<CreditBalance>(`${API_BASE_URL}/credits/balance`),
        apiFetch<PaymentRecord[]>(`${API_BASE_URL}/payments/history`),
      ]);

      const balance = balanceRes.status === 'fulfilled' ? balanceRes.value : null;
      const history =
        historyRes.status === 'fulfilled'
          ? Array.isArray(historyRes.value)
            ? historyRes.value
            : []
          : [];

      const totalSpent = history
        .filter((p) => p.status === 'completed')
        .reduce((sum, p) => sum + (p.amount ?? 0), 0);

      const pendingPayments = history.filter((p) => p.status === 'pending').length;

      setOverview({
        totalSpent,
        activeSubscriptions: 0,
        creditBalance: balance?.balance ?? 0,
        pendingPayments,
      });
    } catch (err: any) {
      setOverviewError(err.message || 'Failed to load payment overview');
    } finally {
      setIsLoadingOverview(false);
    }
  }, [apiFetch]);

  const fetchPaymentHistory = useCallback(async () => {
    setIsLoadingHistory(true);
    setHistoryError(null);
    try {
      const data = await apiFetch<PaymentRecord[]>(`${API_BASE_URL}/payments/history`);
      setPayments(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setHistoryError(err.message || 'Failed to load payment history');
      setPayments([]);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [apiFetch]);

  const fetchSubscriptions = useCallback(async () => {
    setIsLoadingSubscriptions(true);
    setSubscriptionsError(null);
    try {
      const data = await apiFetch<Subscription[]>(`${API_BASE_URL}/payments/subscription`);
      const subs = Array.isArray(data) ? data : [];
      setSubscriptions(subs);
      setOverview((prev) => ({
        ...prev,
        activeSubscriptions: subs.filter((s) => s.status === 'active').length,
      }));
    } catch (err: any) {
      setSubscriptionsError(err.message || 'Failed to load subscriptions');
      setSubscriptions([]);
    } finally {
      setIsLoadingSubscriptions(false);
    }
  }, [apiFetch]);

  const fetchCreditPackages = useCallback(async () => {
    setIsLoadingPackages(true);
    setPackagesError(null);
    try {
      const data = await apiFetch<CreditPackage[]>(`${API_BASE_URL}/credits/packages`);
      setCreditPackages(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setPackagesError(err.message || 'Failed to load credit packages');
      setCreditPackages([]);
    } finally {
      setIsLoadingPackages(false);
    }
  }, [apiFetch]);

  const refreshAll = useCallback(() => {
    fetchOverview();
    fetchPaymentHistory();
    fetchSubscriptions();
    fetchCreditPackages();
  }, [fetchOverview, fetchPaymentHistory, fetchSubscriptions, fetchCreditPackages]);

  useEffect(() => {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Filtering & Sorting ───────────────────────────────────────────────

  const filteredPayments = useMemo(() => {
    let list = [...payments];

    if (statusFilter !== 'all') {
      list = list.filter((p) => p.status === statusFilter);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (p) =>
          p.description?.toLowerCase().includes(q) ||
          p.reference?.toLowerCase().includes(q) ||
          p.method?.toLowerCase().includes(q) ||
          formatCurrency(p.amount).toLowerCase().includes(q),
      );
    }

    list.sort((a, b) => {
      const da = new Date(a.date).getTime();
      const db = new Date(b.date).getTime();
      return sortDirection === 'desc' ? db - da : da - db;
    });

    return list;
  }, [payments, statusFilter, searchQuery, sortDirection]);

  const paginatedPayments = useMemo(
    () => filteredPayments.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredPayments, page, rowsPerPage],
  );

  // ─── Handlers ──────────────────────────────────────────────────────────

  const handlePurchasePackage = async (pkg: CreditPackage) => {
    try {
      const res = await fetch(`${API_BASE_URL}/payments/create-checkout-session`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ packageId: pkg.id, credits: pkg.credits, amount: pkg.price }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        showSuccess(`Initiated purchase of ${pkg.name}`);
      }
    } catch {
      showError('Failed to initiate checkout. Please try again.');
    }
  };

  // ─── Sub-components ────────────────────────────────────────────────────

  const OverviewCard: React.FC<{
    title: string;
    value: string;
    subtitle?: string;
    icon: React.ReactElement;
    color: string;
    loading: boolean;
  }> = ({ title, value, subtitle, icon, color, loading }) => (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 3,
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: `0 8px 24px ${alpha(color, 0.15)}`,
          transform: 'translateY(-2px)',
          borderColor: alpha(color, 0.4),
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 0.5 }}>
              {title}
            </Typography>
            {loading ? (
              <Skeleton variant="text" width={120} height={42} />
            ) : (
              <Typography variant="h4" sx={{ fontWeight: 700, color }}>
                {value}
              </Typography>
            )}
          </Box>
          <Avatar
            sx={{
              bgcolor: alpha(color, 0.1),
              color,
              width: 48,
              height: 48,
            }}
          >
            {icon}
          </Avatar>
        </Box>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  const StatusChip: React.FC<{ status: string }> = ({ status }) => {
    const cfg = statusConfig[status] ?? statusConfig.pending;
    return (
      <Chip
        icon={cfg.icon}
        label={cfg.label}
        color={cfg.color}
        size="small"
        variant="outlined"
        sx={{ fontWeight: 600, borderRadius: '8px' }}
      />
    );
  };

  const ErrorState: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => (
    <Alert
      severity="error"
      action={
        <Button color="inherit" size="small" onClick={onRetry}>
          Retry
        </Button>
      }
      sx={{ borderRadius: 2, mb: 2 }}
    >
      <AlertTitle>Error</AlertTitle>
      {message}
    </Alert>
  );

  const SkeletonTable: React.FC<{ rows?: number }> = ({ rows = 5 }) => (
    <Box>
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} variant="rectangular" height={52} sx={{ mb: 1, borderRadius: 1 }} />
      ))}
    </Box>
  );

  // ─── Render ────────────────────────────────────────────────────────────

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* ── Hero Section ─────────────────────────────────────────────────── */}
      <Box
        sx={{
          backgroundImage: 'url("https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1920&q=80")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          color: 'white',
          minHeight: 200,
          py: { xs: 4, md: 5 },
          px: { xs: 2, md: 4 },
          pb: 7,
        }}
      >
        <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.7)' }} />}
            sx={{ mb: 2 }}
          >
            <Link
              onClick={() => navigate('/')}
              underline="hover"
              sx={{ display: 'flex', alignItems: 'center', color: 'rgba(255,255,255,0.8)', fontSize: '0.875rem', cursor: 'pointer' }}
            >
              <HomeIcon sx={{ mr: 0.5, fontSize: 18 }} />
              Home
            </Link>
            <Link
              onClick={() => navigate('/credits')}
              underline="hover"
              sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.875rem', cursor: 'pointer' }}
            >
              Credits
            </Link>
            <Typography sx={{ color: 'white', fontWeight: 600, fontSize: '0.875rem' }}>History</Typography>
          </Breadcrumbs>

          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}
          >
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5, letterSpacing: '-0.02em', textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}>
                Payment History
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.85, maxWidth: 600, textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}>
                View your Tripnovac credit purchases, transactions, and billing history.
              </Typography>
            </Box>
            <Tooltip title="Refresh all data">
              <IconButton
                onClick={refreshAll}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.15)',
                  color: 'white',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 2, md: 4 }, pb: 6 }}>
        {/* ── Payment Overview Cards ─────────────────────────────────────── */}
        {overviewError && <ErrorState message={overviewError} onRetry={fetchOverview} />}

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <OverviewCard
              title="Total Spent"
              value={formatCurrency(overview.totalSpent)}
              subtitle="Lifetime total"
              icon={<TrendingUpIcon />}
              color={theme.palette.primary.main}
              loading={isLoadingOverview}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <OverviewCard
              title="Active Subscriptions"
              value={String(overview.activeSubscriptions)}
              subtitle="Current plans"
              icon={<SubscriptionsIcon />}
              color={theme.palette.secondary.main}
              loading={isLoadingOverview || isLoadingSubscriptions}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <OverviewCard
              title="Credit Balance"
              value={formatCurrency(overview.creditBalance)}
              subtitle="Available credits"
              icon={<WalletIcon />}
              color={theme.palette.success.main}
              loading={isLoadingOverview}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <OverviewCard
              title="Pending Payments"
              value={String(overview.pendingPayments)}
              subtitle="Awaiting confirmation"
              icon={<WarningIcon />}
              color={theme.palette.warning.main}
              loading={isLoadingOverview}
            />
          </Grid>
        </Grid>

        {/* ── Tab Navigation ─────────────────────────────────────────────── */}
        <Paper
          elevation={0}
          sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3, mb: 3, overflow: 'hidden' }}
        >
          <Tabs
            value={activeTab}
            onChange={(_, v) => {
              setActiveTab(v);
              setPage(0);
            }}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              px: 2,
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 56 },
            }}
          >
            <Tab icon={<HistoryIcon />} iconPosition="start" label="Payment History" />
            <Tab icon={<SubscriptionsIcon />} iconPosition="start" label="Active Subscriptions" />
            <Tab icon={<ShoppingCartIcon />} iconPosition="start" label="Credit Packages" />
          </Tabs>
        </Paper>

        {/* ── Tab 0: Payment History ─────────────────────────────────────── */}
        {activeTab === 0 && (
          <Paper
            elevation={0}
            sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3, overflow: 'hidden' }}
          >
            {/* Toolbar */}
            <Box
              sx={{
                p: 2,
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                alignItems: 'center',
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}
            >
              <TextField
                size="small"
                placeholder="Search transactions…"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(0);
                }}
                sx={{ minWidth: 240, flex: '1 1 240px' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />

              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                {(['all', 'completed', 'pending', 'failed', 'refunded'] as PaymentStatusFilter[]).map((s) => (
                  <Chip
                    key={s}
                    label={s.charAt(0).toUpperCase() + s.slice(1)}
                    variant={statusFilter === s ? 'filled' : 'outlined'}
                    color={
                      s === 'all'
                        ? 'default'
                        : s === 'completed'
                        ? 'success'
                        : s === 'pending'
                        ? 'warning'
                        : s === 'failed'
                        ? 'error'
                        : 'info'
                    }
                    size="small"
                    onClick={() => {
                      setStatusFilter(s);
                      setPage(0);
                    }}
                    sx={{ fontWeight: 600, borderRadius: '8px', cursor: 'pointer' }}
                  />
                ))}
              </Stack>

              <Tooltip title="Toggle sort order">
                <IconButton
                  size="small"
                  onClick={() => setSortDirection((d) => (d === 'desc' ? 'asc' : 'desc'))}
                >
                  {sortDirection === 'desc' ? (
                    <ArrowDownwardIcon fontSize="small" />
                  ) : (
                    <ArrowUpwardIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            </Box>

            {historyError && <ErrorState message={historyError} onRetry={fetchPaymentHistory} />}

            {isLoadingHistory ? (
              <Box sx={{ p: 2 }}>
                <SkeletonTable rows={6} />
              </Box>
            ) : filteredPayments.length === 0 ? (
              <Box sx={{ p: 6, textAlign: 'center' }}>
                <ReceiptIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No transactions found
                </Typography>
                <Typography variant="body2" color="text.disabled">
                  {searchQuery || statusFilter !== 'all'
                    ? 'Try adjusting your filters or search query.'
                    : 'Your payment history will appear here once you make a transaction.'}
                </Typography>
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow
                        sx={{ '& th': { fontWeight: 700, bgcolor: alpha(theme.palette.primary.main, 0.04) } }}
                      >
                        <TableCell>Date</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Method</TableCell>
                        <TableCell>Reference</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedPayments.map((p) => (
                        <TableRow key={p.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {formatDate(p.date)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatDateTime(p.date).split(',').pop()?.trim()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {p.description || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                color:
                                  p.status === 'refunded'
                                    ? theme.palette.info.main
                                    : p.status === 'failed'
                                    ? theme.palette.error.main
                                    : theme.palette.text.primary,
                              }}
                            >
                              {p.status === 'refunded' ? '-' : ''}
                              {formatCurrency(p.amount)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <StatusChip status={p.status} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {p.method || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace' }}>
                              {p.reference || '—'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={filteredPayments.length}
                  page={page}
                  onPageChange={(_, p) => setPage(p)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                />
              </>
            )}
          </Paper>
        )}

        {/* ── Tab 1: Active Subscriptions ────────────────────────────────── */}
        {activeTab === 1 && (
          <Box>
            {subscriptionsError && <ErrorState message={subscriptionsError} onRetry={fetchSubscriptions} />}

            {isLoadingSubscriptions ? (
              <Grid container spacing={3}>
                {[1, 2, 3].map((i) => (
                  <Grid item xs={12} md={4} key={i}>
                    <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 3 }} />
                  </Grid>
                ))}
              </Grid>
            ) : subscriptions.length === 0 ? (
              <Paper
                elevation={0}
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 3,
                  p: 6,
                  textAlign: 'center',
                }}
              >
                <SubscriptionsIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Active Subscriptions
                </Typography>
                <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
                  You don't have any active subscription plans. Browse our credit packages to get started.
                </Typography>
                <Button variant="contained" onClick={() => setActiveTab(2)} startIcon={<ShoppingCartIcon />}>
                  Browse Packages
                </Button>
              </Paper>
            ) : (
              <Grid container spacing={3}>
                {subscriptions.map((sub) => {
                  const isActive = sub.status === 'active';
                  return (
                    <Grid item xs={12} md={4} key={sub.id}>
                      <Card
                        elevation={0}
                        sx={{
                          height: '100%',
                          border: `1px solid ${isActive ? theme.palette.success.light : theme.palette.divider}`,
                          borderRadius: 3,
                          position: 'relative',
                          overflow: 'visible',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            boxShadow: theme.shadows[6],
                            transform: 'translateY(-2px)',
                          },
                        }}
                      >
                        {isActive && (
                          <Chip
                            label="Active"
                            color="success"
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: -12,
                              right: 16,
                              fontWeight: 700,
                              borderRadius: '8px',
                            }}
                          />
                        )}
                        <CardContent sx={{ p: 3 }}>
                          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                            {sub.name}
                          </Typography>
                          <Chip
                            label={sub.plan}
                            size="small"
                            variant="outlined"
                            sx={{ mb: 2, borderRadius: '6px' }}
                          />
                          <Typography
                            variant="h4"
                            sx={{ fontWeight: 800, color: theme.palette.primary.main, mb: 0.5 }}
                          >
                            {formatCurrency(sub.amount)}
                            <Typography
                              component="span"
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontWeight: 400, ml: 0.5 }}
                            >
                              /{sub.interval}
                            </Typography>
                          </Typography>

                          <Divider sx={{ my: 2 }} />

                          <Stack spacing={1}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2" color="text.secondary">
                                Started
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {formatDate(sub.startDate)}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2" color="text.secondary">
                                Next Billing
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {formatDate(sub.nextBillingDate)}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2" color="text.secondary">
                                Status
                              </Typography>
                              <Chip
                                label={sub.status.charAt(0).toUpperCase() + sub.status.slice(1)}
                                size="small"
                                color={
                                  sub.status === 'active'
                                    ? 'success'
                                    : sub.status === 'paused'
                                    ? 'warning'
                                    : sub.status === 'cancelled'
                                    ? 'error'
                                    : 'default'
                                }
                                variant="outlined"
                                sx={{ fontWeight: 600, borderRadius: '6px' }}
                              />
                            </Box>
                          </Stack>

                          {sub.features && sub.features.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 600, mb: 1, display: 'block' }}
                              >
                                Includes:
                              </Typography>
                              {sub.features.map((f, i) => (
                                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                  <CheckCircleIcon sx={{ fontSize: 14, color: 'success.main' }} />
                                  <Typography variant="caption">{f}</Typography>
                                </Box>
                              ))}
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Box>
        )}

        {/* ── Tab 2: Credit Packages ─────────────────────────────────────── */}
        {activeTab === 2 && (
          <Box>
            {packagesError && <ErrorState message={packagesError} onRetry={fetchCreditPackages} />}

            {isLoadingPackages ? (
              <Grid container spacing={3}>
                {[1, 2, 3, 4].map((i) => (
                  <Grid item xs={12} sm={6} md={3} key={i}>
                    <Skeleton variant="rectangular" height={320} sx={{ borderRadius: 3 }} />
                  </Grid>
                ))}
              </Grid>
            ) : creditPackages.length === 0 ? (
              <Paper
                elevation={0}
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 3,
                  p: 6,
                  textAlign: 'center',
                }}
              >
                <ShoppingCartIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Packages Available
                </Typography>
                <Typography variant="body2" color="text.disabled">
                  Credit packages are currently unavailable. Please check back later.
                </Typography>
              </Paper>
            ) : (
              <Grid container spacing={3}>
                {creditPackages.map((pkg) => (
                  <Grid item xs={12} sm={6} md={3} key={pkg.id}>
                    <Card
                      elevation={0}
                      sx={{
                        height: '100%',
                        border: pkg.popular
                          ? `2px solid ${theme.palette.primary.main}`
                          : `1px solid ${theme.palette.divider}`,
                        borderRadius: 3,
                        position: 'relative',
                        overflow: 'visible',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: theme.shadows[8],
                          transform: 'translateY(-4px)',
                        },
                      }}
                    >
                      {pkg.popular && (
                        <Chip
                          icon={<StarIcon sx={{ fontSize: 14 }} />}
                          label="Most Popular"
                          color="primary"
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: -12,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            fontWeight: 700,
                            borderRadius: '8px',
                          }}
                        />
                      )}
                      <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, textAlign: 'center' }}>
                          {pkg.name}
                        </Typography>

                        <Box sx={{ textAlign: 'center', mb: 2 }}>
                          <Typography variant="h3" sx={{ fontWeight: 800, color: theme.palette.primary.main }}>
                            {formatCurrency(pkg.price)}
                          </Typography>
                          {pkg.description && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              {pkg.description}
                            </Typography>
                          )}
                        </Box>

                        <Box
                          sx={{
                            bgcolor: alpha(theme.palette.primary.main, 0.06),
                            borderRadius: 2,
                            p: 2,
                            textAlign: 'center',
                            mb: 2,
                          }}
                        >
                          <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                            {pkg.credits.toLocaleString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            credits
                          </Typography>
                          {pkg.bonus != null && pkg.bonus > 0 && (
                            <Chip
                              label={`+${pkg.bonus} bonus`}
                              color="success"
                              size="small"
                              sx={{ ml: 1, fontWeight: 600, borderRadius: '6px' }}
                            />
                          )}
                        </Box>

                        {pkg.features && pkg.features.length > 0 && (
                          <Box sx={{ flexGrow: 1, mb: 2 }}>
                            {pkg.features.map((f, i) => (
                              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                                <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                                <Typography variant="body2">{f}</Typography>
                              </Box>
                            ))}
                          </Box>
                        )}
                      </CardContent>
                      <CardActions sx={{ p: 3, pt: 0 }}>
                        <Button
                          variant={pkg.popular ? 'contained' : 'outlined'}
                          fullWidth
                          size="large"
                          onClick={() => handlePurchasePackage(pkg)}
                          startIcon={<CreditCardIcon />}
                          sx={{ borderRadius: 2, fontWeight: 700, py: 1.2 }}
                        >
                          Purchase
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PaymentPage;
